"use client";

import { setDragCursor } from "@/components/ui/drag-cursor";
import { useCallback, useEffect, useRef, useState } from "react";
import type { MouseEvent as ReactMouseEvent } from "react";

const clamp = ({
  value,
  min,
  max,
}: {
  value: number;
  min: number;
  max: number;
}) => Math.max(min, Math.min(max, value));

export const useResizableRailWidth = ({
  defaultWidth,
  minWidth,
  maxWidth,
  side,
}: {
  defaultWidth: number;
  minWidth: number;
  maxWidth: number;
  side: "left" | "right";
}) => {
  const [width, setWidth] = useState(defaultWidth);
  const [isResizing, setIsResizing] = useState(false);
  const dragStartX = useRef(0);
  const dragStartWidth = useRef(defaultWidth);

  const onResizeStart = useCallback(
    (event: ReactMouseEvent) => {
      event.preventDefault();
      dragStartX.current = event.clientX;
      dragStartWidth.current = width;
      setIsResizing(true);
    },
    [width]
  );

  useEffect(() => {
    if (!isResizing) return;

    setDragCursor("col-resize");
    document.body.style.userSelect = "none";

    const handleMouseMove = (event: MouseEvent) => {
      const deltaX = event.clientX - dragStartX.current;
      const direction = side === "left" ? 1 : -1;
      setWidth(
        clamp({
          value: dragStartWidth.current + deltaX * direction,
          min: minWidth,
          max: maxWidth,
        })
      );
    };

    const handleMouseUp = () => setIsResizing(false);

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      setDragCursor(null);
      document.body.style.userSelect = "";
    };
  }, [isResizing, maxWidth, minWidth, side]);

  return {
    width,
    widthStyle: `${width}px`,
    isResizing,
    onResizeStart,
  };
};
